import React, { useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom'
import { DateTime } from 'luxon';

// Material-Ui Components
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import ViewIcon from '@material-ui/icons/KeyboardArrowRight';
import AcceptIcon from '@material-ui/icons/Check';
import RestoreIcon from '@material-ui/icons/Restore';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import CloseIcon from '@material-ui/icons/Close';
import Pagination from '@material-ui/lab/Pagination';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';


// Local imports
import useApi from 'app/context/useApi';
import GroupSelect from 'app/components/group/GroupSelect';
import Loading from 'app/components/loaders/Loading';
import useQuery from 'app/hooks/useQuery';


const useRequestStyles = makeStyles(theme => ({
    paper: {
        marginTop: theme.spacing(3)
    },
    tabs: {
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    tableCell: {
        whiteSpace: `nowrap`,
    },
    tableCellNotes: {
        whiteSpace: `nowrap`,
        overflow: `hidden`,
        textOverflow: `ellipsis`,
        maxWidth: '1px',
        minWidth: '1px',
        width: `100%`
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    footer: {
        margin: theme.spacing(3),
    },
    link: {
        textDecoration: "none"
    }
}));

export default function AccessRequests(props) {

    const { limit } = props

    const [ requests, setRequests ] = useState({active: [], rejected: []});
    const [ requestsLoading, setRequestsLoading ] = useState(false);
    const [ requestTab, setRequestTab ] = useState('active');
    const [ page, setPage ] = useState({active: 1, rejected: 1});
    const [ groupSelectDialogOpen, setGroupSelectDialogOpen ] = useState(false);
    const [ activeRequest, setActiveRequest ] = useState(null);
    const [ levelID, setLevelID ] = useState(0);

    const classes = useRequestStyles();
    const api = useApi();
    const query = useQuery();
    const tab = query.get('tab');

    const rows = limit ? 5 : 10;
    const totalRequests = {
        active: requests['active'] ? requests['active'].length : 0,
        rejected: requests['rejected'] ? requests['rejected'].length : 0
     }


    const getRequests = () => {
        setRequestsLoading(true);
        api.get('/admin/club/access-requests').then(res => setRequests(res.requests)).finally(() => setRequestsLoading(false));
    }

    useEffect(() => {
        getRequests();
        handleQuery();
    }, []);

    const handleRequestTabChange = (event, value) => {
        setRequestTab(value);
    };

    const handleQuery = () => {
        tab === 'rejected'?
            setRequestTab('rejected') :
            setRequestTab('active')
    };

    const handlePageChange = (event, value) => {
        setPage({...page, [requestTab] : value});
    };

    const postAccept = () => {
        setRequestsLoading(true);

        api.post(`/admin/club/accept-access/`, {
            id: activeRequest.id,
            user_id: activeRequest.user_id,
            level_id: levelID
        }).then(() => {
            setGroupSelectDialogOpen(false);
            getRequests();
        }).finally(() => setRequestsLoading(false))
    }

    const postReject = (id) => {
        setRequestsLoading(true);
        api.post(`/admin/club/reject-access/`, {id: id})
            .then(getRequests)
            .finally(() => setRequestsLoading(false))
    }

    const postRestore = (id) => {
        setRequestsLoading(true);
        api.post(`/admin/club/restore-access/`, {id: id})
            .then(getRequests)
            .finally(() => setRequestsLoading(false))
    }

    const postDelete = (id) => {
        setRequestsLoading(true);
        api.post(`/admin/club/delete-access/`, {id: id})
            .then(getRequests)
            .finally(() => setRequestsLoading(false))
    }

    const handleDialogOpen = (id) => {
        setGroupSelectDialogOpen(true);
        setActiveRequestID(id);
    }

    const handleDialogClose = () => {
        setGroupSelectDialogOpen(false);
        setActiveRequestID(0);
    }

    const handleGroupSelect = id => {
        setLevelID(id);
    }

    return (
        <Paper className={classes.paper}>
            <Loading isLoading={requestsLoading}/>
            <Tabs
                value={requestTab}
                indicatorColor="secondary"
                textColor="secondary"
                onChange={handleRequestTabChange}
                aria-label="Tasks"
                className={classes.tabs}
                >
                <Tab value="active" label="Active Requests" disabled={requestsLoading}/>
                <Tab value="rejected" label="Rejected Requests" disabled={requestsLoading}/>
            </Tabs>
            {requests[requestTab] && requests[requestTab].length ? (
                <>
                <Table aria-label="Access Requests Table">
                    <TableBody>
                        {requests[requestTab].slice(((page[requestTab]-1)*rows), (((page[requestTab]-1)*rows) + rows)).map((request) => (
                            <TableRow key={request.id}>
                                <TableCell component="th" scope="row" className={classes.tableCell}>{request.user.full_name}</TableCell>
                                <TableCell className={classes.tableCell}>{DateTime.fromISO(request.created_at).toLocaleString(DateTime.DATE_MED)}</TableCell>
                                <TableCell className={classes.tableCellNotes}>{request.meta.notes}</TableCell>
                                {requestTab === 'active' ? (
                                    <>
                                    <TableCell align="right" padding="none" className={classes.tableCell}>
                                    <Tooltip title="Accept Request">
                                        <IconButton
                                            aria-label="Accept"
                                            onClick={() => handleDialogOpen(request.id)}
                                        >
                                            <AcceptIcon />
                                        </IconButton>
                                        {/* <GroupSelectDialog open={groupSelectDialogOpen} onClose={handleDialogClose} id={request.id}/> */}
                                    </Tooltip>
                                    <Tooltip title="Reject Request">
                                        <IconButton
                                            aria-label="Reject"
                                            onClick={() => postReject(request.id)}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View Details">
                                        <IconButton aria-label="Open" component={RouterLink} to={`/admin/access-requests/${request.id}`}>
                                            <ViewIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                                </>
                                ) : (
                                    <TableCell align="right" padding="none" className={classes.tableCell}>
                                    <Tooltip title="Restore Request">
                                        <IconButton
                                            aria-label="Restore"
                                            onClick={() => postRestore(request.id)}
                                        >
                                            <RestoreIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Permanently">
                                        <IconButton
                                            aria-label="Delete"
                                            onClick={() => postDelete(request.id)}
                                        >
                                            <DeleteForeverIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View Details">
                                        <IconButton aria-label="Open" component={RouterLink} to={`/admin/access-requests/${request.id}`}>
                                            <ViewIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {(limit && requests[requestTab].length > 5) &&
                        <Grid item xs={12}container justify="flex-end" alignItems="center">
                            <Typography color="textSecondary" variant="subtitle1" className={classes.footer}>
                                <Link
                                    color="secondary"
                                    component={RouterLink}
                                    to={requestTab==='active' ? "/admin/access-requests" : "/admin/access-requests?tab=rejected"} >
                                        You have {totalRequests[requestTab]} total requests. View All
                                </Link>
                            </Typography>
                        </Grid>
                }
                {!limit && requests[requestTab].length &&
                    <Grid item xs={12}container justify="center" alignItems="center">
                        <Pagination
                            className={classes.footer}
                            count={Math.ceil((requests[requestTab].length) / rows)}
                            page={page[requestTab]}
                            onChange={handlePageChange}
                        />
                    </Grid>
                }
                </>
            ) : (
                <Typography align="center" color="textSecondary" variant="subtitle2" className={classes.empty}>
                    No {requestTab} access requests.
                </Typography>
            )}
            <Dialog
                open={groupSelectDialogOpen}
                onClose={handleDialogClose}
                fullWidth
                maxWidth="sm"
                aria-labelledby="select-level-title"
                >
                <DialogTitle id="select-level-title">
                    Select Membership Level
                </DialogTitle>
                <DialogContent>
                    <GroupSelect
                        variant='level'
                        onSelect={handleGroupSelect}
                        />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDialogClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={postAccept}>
                        Accept Request
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
